/*
 * paneEditorLifecycle — geteilte Lifecycle-Helper fuer Pane-Editoren
 * (MemoryEditor, PersonalDictEditor — kuenftig auch ein Pro-Projekt-
 * Ignore-Editor in Phase 4.5).
 *
 * Hintergrund (Review 2026-05-17, S2-2): jeder Pane-Editor war bisher ein
 * hand-gepflegter Wrapper um eine `ProseEditor`-Instanz mit weitgehend
 * identischer Open/Close/Bounds-Logik. Ohne extrahierten Helper droht beim
 * dritten Wrapper der dritte Fork mit Drift — gemeinsame Edge-Cases (Active-
 * Pane wiederherstellen, currentStation reset, Outline/Context-Pane-Breite 0)
 * waeren dann an drei Stellen einzeln zu pflegen.
 *
 * Bewusste Beschraenkung: dieser Helper kennt KEINE Persistence-Strategie,
 * KEIN Spellcheck-Verkabeln, KEIN Lock-Handling. Das ist alles wrapper-
 * spezifisch und bleibt in MemoryEditor.js / PersonalDictEditor.js. Hier
 * liegen nur die rein strukturellen Pane-Operationen.
 */

import { ProseEditor } from './ProseEditor.js'
import { PaneId, PaneState } from '../config.js'

/**
 * Macht einen versteckten Editor-Pane sichtbar (FULLSCREEN), legt eine frische
 * ProseEditor-Instanz an und macht den Pane zum aktiven Editor-Pane.
 *
 * @param {object} opts
 * @param {object}      opts.layout           - LayoutManager
 * @param {string}      opts.paneId           - PaneId-Konstante (z.B. PaneId.MEMORY)
 * @param {object|null} [opts.renderer]       - Renderer-Referenz fuer Proportional
 * @returns {{ proseEditor: ProseEditor, previousActivePane: string|null }}
 */
export function attachProseEditorToPane({ layout, paneId, renderer = null }) {
    if (!layout || !layout.panes || !layout.panes[paneId]) {
        throw new Error(`attachProseEditorToPane: layout missing pane ${paneId}`)
    }

    const previousActivePane = layout.getActiveEditorPane
        ? layout.getActiveEditorPane()
        : null

    const pane = layout.panes[paneId]
    pane.state = PaneState.FULLSCREEN
    layout.calculateBounds()

    // Pane zum aktiven Editor-Pane machen — sonst rendert renderCursorLayer
    // den Cursor in einem unsichtbaren Pane (MAIN). currentStation auf null
    // zuruecksetzen, sonst zeigt sie noch auf eine Station des vorherigen
    // Panes und der Cursor-Layer ueberspringt den neu geoeffneten Pane.
    if (typeof layout.setActivePane === 'function') {
        layout.setActivePane(paneId)
    }
    layout.currentStation = null

    const proseEditor = new ProseEditor(pane.innerBounds)
    if (renderer) proseEditor.view.renderer = renderer
    // Outline/Context-Pane-Breiten auf 0 — Pane-Editoren haben nur den Main-
    // Pane-Bereich, sonst rendert die View Note/Info-Spalten leer ins
    // Off-Screen und triggert ggf. Filter-Modi.
    proseEditor.view.outlinePaneWidth = 0
    proseEditor.view.contextPaneWidth = 0

    layout.isDirty = true
    layout.borderDirty = true

    return { proseEditor, previousActivePane }
}

/**
 * Versteckt einen Editor-Pane (HIDDEN) und stellt den vorher aktiven Pane
 * wieder her. Fallback auf PaneId.MAIN, wenn der vorher aktive Pane nicht
 * mehr sichtbar oder nicht mehr im Layout ist.
 *
 * @param {object} opts
 * @param {object}      opts.layout
 * @param {string}      opts.paneId
 * @param {string|null} opts.previousActivePane
 */
export function detachProseEditorFromPane({ layout, paneId, previousActivePane }) {
    if (!layout || !layout.panes || !layout.panes[paneId]) return

    const pane = layout.panes[paneId]
    pane.state = PaneState.HIDDEN
    layout.calculateBounds()

    if (typeof layout.setActivePane === 'function') {
        const target = (previousActivePane
            && previousActivePane !== paneId
            && layout.panes[previousActivePane]
            && layout.panes[previousActivePane].isVisible())
            ? previousActivePane
            : PaneId.MAIN
        layout.setActivePane(target)
    }

    layout.isDirty = true
    layout.borderDirty = true
}

/**
 * Synchronisiert die ProseEditor-Bounds mit dem aktuellen Pane-Layout, falls
 * sich das Browser-Fenster gezogen hat. No-Op, wenn der Pane unsichtbar ist
 * oder die Bounds unveraendert sind.
 *
 * @param {object} opts
 * @param {ProseEditor} opts.proseEditor
 * @param {object}      opts.layout
 * @param {string}      opts.paneId
 * @returns {boolean} true wenn ein resize() ausgeloest wurde
 */
export function syncProseEditorBounds({ proseEditor, layout, paneId }) {
    if (!proseEditor || !layout || !layout.panes || !layout.panes[paneId]) return false
    const pane = layout.panes[paneId]
    if (!pane.isVisible()) return false
    const bounds = pane.innerBounds
    const cur = proseEditor.bounds
    if (cur.width !== bounds.width || cur.height !== bounds.height
        || cur.x !== bounds.x || cur.y !== bounds.y
    ) {
        proseEditor.resize(bounds, 0, 0)
        proseEditor.view.markDirty()
        return true
    }
    return false
}

/**
 * True wenn das Modell genau leer ist (kein Paragraph oder ein einzelner
 * mit leerem text/note/info).
 *
 * @param {ProseEditor|null} proseEditor
 * @returns {boolean}
 */
export function isProseEditorModelEmpty(proseEditor) {
    const model = proseEditor && proseEditor.model
    if (!model) return true
    const paras = model.paragraphs
    if (!Array.isArray(paras) || paras.length === 0) return true
    if (paras.length > 1) return false
    const first = paras[0]
    if (!first) return true
    const text = first.text || ''
    const note = first.note || ''
    const info = first.info || ''
    return text === '' && note === '' && info === ''
}

/**
 * Filtert Block-Strukturierungs- und Suche-Shortcuts, die in einem Pane-Editor
 * keinen Sinn ergeben. Beide aktuellen Wrapper (Memory, PersonalDict) haben
 * diesen Filter-Block — Block-Format-Shortcuts (Cmd+B/I/U/E) bleiben aussen
 * vor, weil Memory sie braucht, PersonalDict nicht.
 *
 * @param {KeyboardEvent} e
 * @returns {boolean} true wenn der Wrapper den Event geschluckt hat (Caller
 *   darf NICHT mehr an proseEditor.input.handleKeydown weiterreichen).
 */
export function applyCommonPaneKeyFilters(e) {
    if (!e) return false
    const cmdKey = e.metaKey || e.ctrlKey

    // Cmd+= / Cmd+Shift+0 / Cmd+# (DE): Separator-Erzeugung.
    if (cmdKey && !e.altKey && (
        e.code === 'Backslash'
        || (e.code === 'Digit0' && e.shiftKey)
        || (e.code === 'Equal' && !e.shiftKey)
    )) {
        e.preventDefault()
        return true
    }
    // Cmd+F: Suche.
    if (cmdKey && !e.altKey && !e.shiftKey
        && (e.key === 'f' || e.key === 'F')
    ) {
        e.preventDefault()
        return true
    }
    // Cmd+Enter: Section-/Active-Collapse.
    if (cmdKey && !e.altKey && !e.shiftKey && e.key === 'Enter') {
        e.preventDefault()
        return true
    }
    // Alt+Enter: Section-Lock-Toggle.
    if (e.altKey && !cmdKey && !e.shiftKey && e.key === 'Enter') {
        e.preventDefault()
        return true
    }
    return false
}

// Source → user-friendly Title fuer den modalen Info-Dialog (showPaneEditorToast).
// Die Source-Strings sind interne Module-Namen ('MemoryEditor', 'PersonalDictEditor');
// dem User wollen wir lesbare Titel zeigen.
const TOAST_DIALOG_TITLES = {
    MemoryEditor: 'AI Memory',
    PersonalDictEditor: 'Personal Dictionary'
}

/**
 * Zeigt eine User-Meldung aus einem Pane-Editor an. Der namensgebende "Toast"
 * existiert in Carbisdale noch nicht (CLAUDE.md "Ideensammlung → Toast-System")
 * — bis dahin laeuft die Meldung als modaler Info-Dialog mit OK-Button, damit
 * sie nicht still in der Konsole verschwindet (Boris hat das Stillschweigen
 * 2026-05-17 ausdruecklich als Bug markiert).
 *
 * Async fire-and-forget: der Aufrufer (z.B. `MemoryEditor._handleSaveError`,
 * `PersonalDictEditor.close` Cap-Pfad) darf nicht auf das User-Acknowledge
 * warten. Wenn bereits ein Dialog offen ist (Konflikt mit Confirm-Dialog
 * aus Cloud-Toggle o.ae.), faellt der Helper still auf `console.warn` zurueck —
 * lieber kein Dialog als ein vergessener `resolve` der vorigen Promise.
 *
 * @param {object|null} dialogManager
 * @param {string}      source   - z.B. 'MemoryEditor', 'PersonalDictEditor'
 * @param {string}      message
 */
export function showPaneEditorToast(dialogManager, source, message) {
    const dialogBusy = dialogManager
        && typeof dialogManager.isDialogOpen === 'function'
        && dialogManager.isDialogOpen()
    if (dialogManager
            && typeof dialogManager.showInfo === 'function'
            && !dialogBusy) {
        const title = TOAST_DIALOG_TITLES[source] || source
        // Fire-and-forget — der modale Dialog soll den Aufrufer nicht blockieren.
        Promise.resolve()
            .then(() => dialogManager.showInfo(title, message))
            .catch(err => console.warn(`[${source}] showInfo failed:`, err && err.message))
        return
    }
    console.warn(`[${source} toast] ${message}`)
}
